if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dotsContainer: this.querySelector('[id^="GalleryDots"]')
        };
        const galleryScroller =
          this.elements.viewer.querySelector('.product__media-list') || this.elements.viewer;
        let activeSlideSyncScheduled = false;
        const scheduleActiveSlideSync = () => {
          if (activeSlideSyncScheduled) return;
          activeSlideSyncScheduled = true;
          requestAnimationFrame(() => {
            activeSlideSyncScheduled = false;
            this.syncActiveSlideToScroll();
          });
        };
        galleryScroller.addEventListener('scroll', scheduleActiveSlideSync);
        galleryScroller.addEventListener('touchend', scheduleActiveSlideSync);

        this.navDesktop = this.dataset.mediaNavTypeDesktop || 'thumbnails';
        this.navMobile = this.dataset.mediaNavTypeMobile || 'thumbnails';
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 200));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        this.addEventListener('gallery:regenerate-dots', this.renderDots.bind(this));
        this.mql.addEventListener('change', this.syncNav.bind(this));
        this.syncNav();
        this.renderDots();
        this.lastActiveSlideIndex = 0;
        this.addEventListener('click', this.revealVideoControls.bind(this));
        /* Nothing plays gallery media on load, so a video on the active slide
           sits behind its poster. Waiting for visibility keeps it off the
           critical path for visitors who never scroll to it. */
        new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          const activeItem = this.elements.viewer.querySelector('.product__media-item.is-active');
          if (activeItem && activeItem.querySelector('.media-type-video')) this.playActiveMedia(activeItem);
        }).observe(this);
      }

      /* A tap brings the native controls back — also the pause mechanism
         WCAG 2.2.2 wants — and they clear again shortly after. */
      revealVideoControls(event) {
        const video = event.target;
        if (!video.matches || !video.matches('video')) return;
        video.controls = true;
        clearTimeout(this.hideControlsTimeout);
        this.hideControlsTimeout = setTimeout(() => {
          video.controls = false;
        }, 3500);
      }

      syncActiveSlideToScroll() {
        const items = this.elements.viewer.querySelectorAll('.product__media-item');
        const viewerRect = this.elements.viewer.getBoundingClientRect();
        let maxVisibleWidth = 0;
        let activeIndex = 0;
        items.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const visibleWidth = Math.max(
            0,
            Math.min(rect.right, viewerRect.right) - Math.max(rect.left, viewerRect.left)
          );
          if (visibleWidth > maxVisibleWidth) {
            maxVisibleWidth = visibleWidth;
            activeIndex = index;
          }
        });
        items.forEach((item, index) => item.classList.toggle('is-active', index === activeIndex));
        this.updateActiveDot();

        /* This gallery drives itself from native scroll position, so Dawn's
           slideChanged event never fires — SliderComponent bails out early
           without the prev/next buttons this theme replaced with its own dots.
           Swiping is therefore the one path that has to start the video
           itself. */
        if (activeIndex === this.lastActiveSlideIndex) return;
        this.lastActiveSlideIndex = activeIndex;
        this.playActiveMedia(items[activeIndex]);
      }

      get visibleSlides() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item')).filter((el) => !el.classList.contains('hide-image'));
      }

      getActiveSlideIndex() {
        const active = this.elements.viewer.querySelector('.product__media-item.is-active');
        const list = this.visibleSlides;
        return Math.max(0, list.indexOf(active));
      }

      syncNav() {
        const isDesktop = this.mql.matches;
        const useDots = isDesktop ? this.navDesktop === 'dots' : this.navMobile === 'dots';
        if (this.elements.dotsContainer) this.elements.dotsContainer.style.display = useDots ? '' : 'none';
        if (useDots) this.renderDots();
      }

      renderDots() {
  if (!this.elements.dotsContainer) return;
  const slides = this.visibleSlides;
  const currentIndex = this.getActiveSlideIndex();
  this.elements.dotsContainer.innerHTML = '';
  const wrapper = document.createElement('div');
  wrapper.className = 'gallery-dots-wrapper';
  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'gallery-dot-arrow gallery-dot-arrow--prev';
  prev.setAttribute('aria-label', 'Previous media');
  prev.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11.9164 3.57716C12.2413 3.25222 12.7662 3.25233 13.0912 3.57716C13.4162 3.90216 13.4162 4.42696 13.0912 4.75196L7.84118 9.99415H7.84997L13.0912 15.2354C13.4162 15.5604 13.4162 16.0852 13.0912 16.4102C12.9246 16.5683 12.7168 16.6522 12.5004 16.6524C12.2837 16.6524 12.0752 16.5768 11.9086 16.4102L6.07458 10.5772C5.74983 10.2522 5.74993 9.72733 6.07458 9.40235L11.9164 3.57716Z" fill="#0C0C0C" fill-opacity="0.2"></path></svg>';
  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'gallery-dot-arrow gallery-dot-arrow--next';
  next.setAttribute('aria-label', 'Next media');
  next.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11.9164 3.57716C12.2413 3.25222 12.7662 3.25233 13.0912 3.57716C13.4162 3.90216 13.4162 4.42696 13.0912 4.75196L7.84118 9.99415H7.84997L13.0912 15.2354C13.4162 15.5604 13.4162 16.0852 13.0912 16.4102C12.9246 16.5683 12.7168 16.6522 12.5004 16.6524C12.2837 16.6524 12.0752 16.5768 11.9086 16.4102L6.07458 10.5772C5.74983 10.2522 5.74993 9.72733 6.07458 9.40235L11.9164 3.57716Z" fill="#0C0C0C" fill-opacity="0.2"></path></svg>'
  const btns = document.createElement('div');
  btns.className = 'gallery-dots-buttons';
  slides.forEach((slide, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gallery-dot';
    b.dataset.index = i.toString();
    b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
    if (i === currentIndex) b.classList.add('active');
    b.addEventListener('click', () => {
      const id = slide.getAttribute('data-media-id');
      this.setActiveMedia(id, false);
    });
    btns.appendChild(b);
  });
  prev.addEventListener('click', () => {
    const i = this.getActiveSlideIndex();
    const t = Math.max(0, i - 1);
    const slide = this.visibleSlides[t];
    if (slide) this.setActiveMedia(slide.getAttribute('data-media-id'), false);
  });
  next.addEventListener('click', () => {
    const i = this.getActiveSlideIndex();
    const t = Math.min(this.visibleSlides.length - 1, i + 1);
    const slide = this.visibleSlides[t];
    if (slide) this.setActiveMedia(slide.getAttribute('data-media-id'), false);
  });
  wrapper.appendChild(prev);
  wrapper.appendChild(btns);
  wrapper.appendChild(next);
  this.elements.dotsContainer.appendChild(wrapper);
  this.updateActiveDot();
}


      onSlideChanged(event) {
        const id = event.detail.currentElement.dataset.mediaId;
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${id}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        this.updateActiveDot();
      }

      updateActiveDot() {
        if (!this.elements.dotsContainer) return;
        const idx = this.getActiveSlideIndex();
        const dots = this.elements.dotsContainer.querySelectorAll('.gallery-dot');
        if (dots.length !== this.visibleSlides.length) {
          this.renderDots();
          return;
        }
        dots.forEach((d, i) => {
          if (i === idx) d.classList.add('active'); else d.classList.remove('active');
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') activeMedia.scrollIntoView({ behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail ? activeThumbnail.dataset.mediaPosition : '');
        }
        this.updateActiveDot();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      /* Every route into a video lands here — thumbnail, dot, variant change,
         swipe, first paint — so the control bar comes off the template before
         loadContent clones it. Stripping it afterwards leaves a frame where
         the bar flashes, and the frame's 18% radius would clip it. */
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem && activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        const template = deferredMedia.querySelector('template');
        const templateVideo = template && template.content.querySelector('video[autoplay]');
        if (templateVideo) {
          templateVideo.removeAttribute('controls');
          /* And the poster. A poster plus preload="none" plus a programmatic
             play leaves WebKit parked in its poster display state: the video
             runs, the timeline advances, but the artwork stays frozen until a
             tap forces it out. With no poster there is nothing to park on and
             the first decoded frame is drawn. */
          templateVideo.removeAttribute('poster');
        }
        deferredMedia.loadContent(false);

        const video = deferredMedia.querySelector('video');
        if (!video) return;
        video.controls = false;
        video.play().catch(() => {
          video.controls = true;
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
